import React from "react";
import {
  CreateGroupModal,
  FieldSelectionModal,
  AddExerciseModal,
  DeleteBlockModal,
} from "../modals";
import {
  useGetMetricsQuery,
  useGetExercisesQuery,
  useGetEquipmentQuery,
  useGetMusclesQuery,
  useDeleteSessionMatriceBlockMutation,
  useCreateExerciseBlockMutation,
  useDeleteSessionMatriceGroupBlockMutation,
} from "../../../../services";
import { toast } from "../../../../core/toast";
import { useSearchParams } from "react-router-dom";

interface MatricBlock {
  bid?: string;
  id: string;
  name: string;
  fields: string[];
  fieldValues?: Record<string, number>;
  workPeriod?: string;
  restPeriod?: string;
  blockTiming?: string;
  restTime?: string;
  type?: "MATRIX" | "GROUP" | "SINGLE";
  workouts?: GroupWorkout[];
  isMaxOrder?: boolean;
}

interface GroupWorkout {
  _id: string;
  workout: {
    _id: string;
    name: string;
    muscles?: any[];
    equipment?: any;
    video?: any;
    thumbnail?: any;
  };
  metrics: Array<{
    _id: string;
    name: string;
    metric: string;
    value: number;
    static: boolean;
  }>;
  workPeriod: number;
  restPeriod: number;
  blockTiming: number;
  order: number;
}

interface MatricSessionProps {
  themeBox: boolean;
  formatBlocks: MatricBlock[];
  setFormatBlocks: React.Dispatch<React.SetStateAction<MatricBlock[]>>;
  sessionFormatBlocks: any[];
  difficulty: "BEGINNER" | "ADVANCED" | "GENERAL" | null;
  sessionFormatId: string;
  setIsDirty: (dirty: boolean) => void;
  setHasTimeErrors?: (hasErrors: boolean) => void;
  showValidationErrors?: boolean;
}

const MetricSessionFormat: React.FC<MatricSessionProps> = ({
  themeBox,
  formatBlocks,
  setFormatBlocks,
  sessionFormatBlocks,
  difficulty,
  sessionFormatId,
  setIsDirty,
  setHasTimeErrors,
  showValidationErrors,
}) => {
  // Add local state for sessionFormatBlocks to update UI without reload
  const [localSessionFormatBlocks, setLocalSessionFormatBlocks] =
    React.useState<any[]>(sessionFormatBlocks);

  // Keep localSessionFormatBlocks in sync with prop unless we just added
  React.useEffect(() => {
    setLocalSessionFormatBlocks(sessionFormatBlocks);
  }, [sessionFormatBlocks]);

  // Calculate existing exercise IDs to disable them in the Add Exercise modal
  const existingExerciseIds = React.useMemo(() => {
    const ids: string[] = [];
    (localSessionFormatBlocks || []).forEach((blk: any) => {
      (blk.workouts || []).forEach((w: any) => {
        const wid = w?.workout?._id || w?.workout;
        if (wid) ids.push(String(wid));
      });
    });
    return ids;
  }, [localSessionFormatBlocks]);

  const [showGroupModal, setShowGroupModal] = React.useState(false);
  const [selectedBlockId, setSelectedBlockId] = React.useState<string | null>(
    null,
  );
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get("category");
  const { data: metricsData } = useGetMetricsQuery();
  const metricsArray = Array.isArray(metricsData) ? metricsData : [];
  const metricFieldOptions: { id: string; name: string }[] = metricsArray
    .map((m: any) => ({
      id: m?._id || m?.id || "",
      name: m?.name || "",
    }))
    .filter((opt: any) => !!opt.id && !!opt.name);

  // Fetch exercises for AddExerciseModal
  const [search, setSearch] = React.useState("");
  const [muscle, setMuscle] = React.useState("");
  const [equipment, setEquipment] = React.useState("");
  const [showAddExerciseModal, setShowAddExerciseModal] = React.useState(false);
  const [addModalSelection, setAddModalSelection] = React.useState<
    string | null
  >(null);
  const { data: exerciseData } = useGetExercisesQuery({
    muscle,
    equipment,
    name: search,
  });
  const exercises = exerciseData || [];
  const filteredExercises = exercises;

  // Local field selection modal state
  const [showFieldModal, setShowFieldModal] = React.useState(false);
  const [modalTargetBlockId, setModalTargetBlockId] = React.useState<
    string | null
  >(null);
  const [modalTargetWorkoutId, setModalTargetWorkoutId] = React.useState<
    string | null
  >(null);
  const [selectedFields, setSelectedFields] = React.useState<string[]>([]);
  const MAX_FIELDS = 5;

  const [expandedBlocks, setExpandedBlocks] = React.useState<
    Record<string, boolean>
  >({});

  const [restTimeVisible, setRestTimeVisible] = React.useState<
    Record<string, boolean>
  >({});

  const [timeErrors, setTimeErrors] = React.useState<Record<string, string>>(
    {},
  );

  // Toggle block expand/collapse
  const toggleBlock = (id: string) => {
    setExpandedBlocks((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Handle increment field value
  const incrementField = (blockId: string, fieldLabel: string) => {
    setIsDirty(true);
    setFormatBlocks((prev) =>
      prev.map((block) => {
        if (block.id === blockId) {
          const currentValue = block.fieldValues?.[fieldLabel] ?? 1;
          return {
            ...block,
            fieldValues: {
              ...block.fieldValues,
              [fieldLabel]: currentValue + 1,
            },
          };
        }
        return block;
      }),
    );
  };

  // Handle decrement field value
  const decrementField = (blockId: string, fieldLabel: string) => {
    setIsDirty(true);
    setFormatBlocks((prev) =>
      prev.map((block) => {
        if (block.id === blockId) {
          const currentValue = block.fieldValues?.[fieldLabel] ?? 1;
          return {
            ...block,
            fieldValues: {
              ...block.fieldValues,
              [fieldLabel]: Math.max(1, currentValue - 1),
            },
          };
        }
        return block;
      }),
    );
  };

  // Handle field value input change
  const handleFieldValueChange = (
    blockId: string,
    fieldLabel: string,
    value: string,
  ) => {
    setIsDirty(true);
    const onlyDigits = value.replace(/\D/g, "");
    const numValue = onlyDigits === "" ? 1 : parseInt(onlyDigits, 10);
    setFormatBlocks((prev) =>
      prev.map((block) => {
        if (block.id === blockId) {
          return {
            ...block,
            fieldValues: {
              ...block.fieldValues,
              [fieldLabel]: Math.max(1, isNaN(numValue) ? 1 : numValue),
            },
          };
        }
        return block;
      }),
    );
  };

  // NEW: Handle increment for group workout metrics
  const incrementGroupWorkoutMetric = (
    blockId: string,
    workoutId: string,
    metricId: string,
  ) => {
    setIsDirty(true);
    setFormatBlocks((prev) =>
      prev.map((block) => {
        if (block.id === blockId && block.workouts) {
          return {
            ...block,
            workouts: block.workouts.map((workout) => {
              if (workout._id === workoutId) {
                return {
                  ...workout,
                  metrics: workout.metrics.map((metric) => {
                    if (metric.metric === metricId) {
                      return { ...metric, value: metric.value + 1 };
                    }
                    return metric;
                  }),
                };
              }
              return workout;
            }),
          };
        }
        return block;
      }),
    );
  };

  // NEW: Handle decrement for group workout metrics
  const decrementGroupWorkoutMetric = (
    blockId: string,
    workoutId: string,
    metricId: string,
  ) => {
    setIsDirty(true);
    setFormatBlocks((prev) =>
      prev.map((block) => {
        if (block.id === blockId && block.workouts) {
          return {
            ...block,
            workouts: block.workouts.map((workout) => {
              if (workout._id === workoutId) {
                return {
                  ...workout,
                  metrics: workout.metrics.map((metric) => {
                    if (metric.metric === metricId) {
                      return {
                        ...metric,
                        value: Math.max(1, metric.value - 1),
                      };
                    }
                    return metric;
                  }),
                };
              }
              return workout;
            }),
          };
        }
        return block;
      }),
    );
  };

  // NEW: Handle metric value change for group workouts
  const handleGroupWorkoutMetricChange = (
    blockId: string,
    workoutId: string,
    metricId: string,
    value: string,
  ) => {
    setIsDirty(true);
    const onlyDigits = value.replace(/\D/g, "");
    const numValue = onlyDigits === "" ? 1 : parseInt(onlyDigits, 10);
    setFormatBlocks((prev) =>
      prev.map((block) => {
        if (block.id === blockId && block.workouts) {
          return {
            ...block,
            workouts: block.workouts.map((workout) => {
              if (workout._id === workoutId) {
                return {
                  ...workout,
                  metrics: workout.metrics.map((metric) => {
                    if (metric.metric === metricId) {
                      return {
                        ...metric,
                        value: Math.max(1, isNaN(numValue) ? 1 : numValue),
                      };
                    }
                    return metric;
                  }),
                };
              }
              return workout;
            }),
          };
        }
        return block;
      }),
    );
  };

  // Helper to format time input
  const formatTimeInput = (value: string): string => {
    let cleaned = value.replace(/[^\d:]/g, "");
    const parts = cleaned.split(":");
    if (parts.length > 3) {
      cleaned = parts.slice(0, 3).join(":");
    }
    const segments = cleaned.split(":");
    const formatted: string[] = [];
    for (let i = 0; i < segments.length && i < 3; i++) {
      let segment = segments[i];
      if (segment.length > 2) {
        segment = segment.slice(0, 2);
      }
      formatted.push(segment);
    }
    return formatted.join(":");
  };

  const validateTime = (value: string): string | null => {
    if (!value || value.trim() === "" || value === "00:00:00") {
      return "Required";
    }
    const parts = value.split(":");
    if (parts.length !== 3) return "Invalid format (HH:MM:SS)";

    const [hRaw, mRaw, sRaw] = parts;
    const h = parseInt(hRaw);
    const m = parseInt(mRaw);
    const s = parseInt(sRaw);

    if (isNaN(h) || isNaN(m) || isNaN(s)) return "Invalid numbers";
    if (h > 23) return "Hours 0-23";
    if (m > 59) return "Min 0-59";
    if (s > 59) return "Sec 0-59";

    return null;
  };

  // Helper to sum two time strings (HH:MM:SS)
  const sumTimeStrings = (
    t1: string = "00:00:00",
    t2: string = "00:00:00",
  ): string => {
    const toSeconds = (t: string) => {
      const [h = "0", m = "0", s = "0"] = t.split(":");
      return (
        (parseInt(h) || 0) * 3600 + (parseInt(m) || 0) * 60 + (parseInt(s) || 0)
      );
    };
    const totalSeconds = toSeconds(t1) + toSeconds(t2);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return [h, m, s].map((v) => String(v).padStart(2, "0")).join(":");
  };

  // Handle time input changes for MATRIX blocks
  const handleTimeChange = (
    blockId: string,
    field: "workPeriod" | "restPeriod" | "blockTiming",
    value: string,
  ) => {
    setIsDirty(true);
    const formattedValue = formatTimeInput(value);

    // Validate as user types for format, but maybe not for completeness yet
    const error = validateTime(formattedValue);
    const errorKey = `${blockId}-${field}`;
    setTimeErrors((prev) => {
      if (!error) {
        const { [errorKey]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [errorKey]: error };
    });

    setFormatBlocks((prev) =>
      prev.map((block) => {
        if (block.id === blockId) {
          const updatedBlock = {
            ...block,
            [field]: formattedValue,
          };

          // Auto-calculate blockTiming if workPeriod or restPeriod changes
          if (field === "workPeriod" || field === "restPeriod") {
            updatedBlock.blockTiming = sumTimeStrings(
              updatedBlock.workPeriod || "00:00:00",
              updatedBlock.restPeriod || "00:00:00",
            );
          }

          return updatedBlock;
        }
        return block;
      }),
    );
  };

  // NEW: Handle time input changes for GROUP workout items
  const handleGroupWorkoutTimeChange = (
    blockId: string,
    workoutId: string,
    field: "workPeriod" | "restPeriod" | "blockTiming",
    value: string,
  ) => {
    setIsDirty(true);
    const cleaned = value.replace(/[^\d:]/g, "");
    const parts = cleaned.split(":").slice(0, 3);
    const normalized = parts.map((p) => p.replace(/\D/g, "").slice(0, 2));
    const next = normalized.join(":");

    const error = validateTime(next);
    const errorKey = `${blockId}-${workoutId}-${field}`;
    setTimeErrors((prev) => {
      if (!error) {
        const { [errorKey]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [errorKey]: error };
    });

    setFormatBlocks((prev) =>
      prev.map((block) => {
        if (block.id === blockId && block.workouts) {
          // Sync changes across ALL workouts in the group
          return {
            ...block,
            workouts: block.workouts.map((workout) => {
              const updatedWorkout = { ...workout, [field]: next } as any;

              // Auto-calculate blockTiming if workPeriod or restPeriod changes
              if (field === "workPeriod" || field === "restPeriod") {
                updatedWorkout.blockTiming = sumTimeStrings(
                  updatedWorkout.workPeriod || "00:00:00",
                  updatedWorkout.restPeriod || "00:00:00",
                );
              }

              return updatedWorkout;
            }),
          };
        }
        return block;
      }),
    );
  };

  // NEW: Handle blur for GROUP workout time fields
  const handleGroupWorkoutTimeBlur = (
    blockId: string,
    workoutId: string,
    field: "workPeriod" | "restPeriod" | "blockTiming",
  ) => {
    const errorKey = `${blockId}-${workoutId}-${field}`;

    setFormatBlocks((prev) =>
      prev.map((block) => {
        if (block.id === blockId && block.workouts) {
          // Find the value from the triggering workout to apply to the whole group
          const triggerWorkout = block.workouts.find((w) => w._id === workoutId);
          if (!triggerWorkout) return block;

          const currentValue = (triggerWorkout as any)[field] || "";
          const parts = currentValue.split(":");
          const paddedParts = [
            (parts[0] || "00").padStart(2, "0"),
            (parts[1] || "00").padStart(2, "0"),
            (parts[2] || "00").padStart(2, "0"),
          ];
          const next = paddedParts.join(":");

          const error = validateTime(next);
          setTimeErrors((prevErr) => {
            if (!error) {
              const { [errorKey]: _, ...rest } = prevErr;
              return rest;
            }
            return { ...prevErr, [errorKey]: error };
          });

          return {
            ...block,
            workouts: block.workouts.map((workout) => {
              const updatedWorkout = { ...workout, [field]: next } as any;

              if (field === "workPeriod" || field === "restPeriod") {
                updatedWorkout.blockTiming = sumTimeStrings(
                  updatedWorkout.workPeriod || "00:00:00",
                  updatedWorkout.restPeriod || "00:00:00",
                );
              }

              return updatedWorkout;
            }),
          };
        }
        return block;
      }),
    );
  };

  // Handle block-level Rest Time changes
  const handleRestTimeChange = (blockId: string, value: string) => {
    setIsDirty(true);
    const cleaned = value.replace(/[^\d:]/g, "");
    const parts = cleaned.split(":").slice(0, 3);
    const normalized = parts.map((p) => p.replace(/\D/g, "").slice(0, 2));
    const next = normalized.join(":");
    setFormatBlocks((prev) =>
      prev.map((block) =>
        block.id === blockId ? { ...block, restTime: next } : block,
      ),
    );
  };

  // Blur formatter for block-level restTime
  const handleRestTimeBlur = (blockId: string) => {
    setFormatBlocks((prev) =>
      prev.map((block) => {
        if (block.id !== blockId) return block;
        const currentValue = block.restTime || "00:00:00";
        const [hRaw = "0", mRaw = "0", sRaw = "0"] = currentValue.split(":");
        const h = Math.min(23, Number(hRaw) || 0);
        const m = Math.min(59, Number(mRaw) || 0);
        const s = Math.min(59, Number(sRaw) || 0);
        const padded = [h, m, s]
          .map((n) => String(n).padStart(2, "0"))
          .join(":");
        return { ...block, restTime: padded };
      }),
    );
  };

  // Handle time input blur for MATRIX blocks
  const handleTimeBlur = (
    blockId: string,
    field: "workPeriod" | "restPeriod" | "blockTiming",
  ) => {
    const errorKey = `${blockId}-${field}`;
    setFormatBlocks((prev) =>
      prev.map((block) => {
        if (block.id === blockId) {
          const currentValue = block[field] || "";
          const parts = currentValue.split(":");
          const paddedParts = [
            (parts[0] || "00").padStart(2, "0"),
            (parts[1] || "00").padStart(2, "0"),
            (parts[2] || "00").padStart(2, "0"),
          ];
          const next = paddedParts.join(":");

          const error = validateTime(next);
          setTimeErrors((prevErr) => {
            if (!error) {
              const { [errorKey]: _, ...rest } = prevErr;
              return rest;
            }
            return { ...prevErr, [errorKey]: error };
          });

          return {
            ...block,
            [field]: next,
          };
        }
        return block;
      }),
    );
  };

  // Report errors to parent
  React.useEffect(() => {
    if (setHasTimeErrors) {
      setHasTimeErrors(Object.keys(timeErrors).length > 0);
    }
  }, [timeErrors, setHasTimeErrors]);

  // Reset local UI when difficulty changes
  React.useEffect(() => {
    setExpandedBlocks({});
    setRestTimeVisible({});
    setSelectedFields([]);
  }, [difficulty]);

  // Use mutation for adding exercise
  const [createExerciseBlock, { isLoading: isCreatingExercise }] =
    useCreateExerciseBlockMutation();

  // Update confirmAddExercise to update localSessionFormatBlocks after add
  const confirmAddExercise = async () => {
    if (!addModalSelection || !sessionFormatId) return;

    try {
      const response = await createExerciseBlock({
        sessionFormatId,
        workout: addModalSelection,
        difficulty: effectiveDifficulty,
      }).unwrap();

      setShowAddExerciseModal(false);
      setAddModalSelection(null);

      // If API returns the new block, append it to localSessionFormatBlocks
      if (response?.result) {
        setLocalSessionFormatBlocks((prev) => [
          ...(prev || []),
          response.result,
        ]);
      }
    } catch (error: any) {
      console.error("Failed to create exercise block:", error);
      const errorMessage =
        error?.data?.message ||
        error?.message ||
        "Failed to create exercise block";
      toast.error(errorMessage);
    }
  };

  // Use localSessionFormatBlocks for rebuilding blocks from API
  React.useEffect(() => {
    const secToHHMMSS = (s: number = 0): string => {
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = Math.max(0, s % 60);
      return [h, m, sec].map((v) => String(v).padStart(2, "0")).join(":");
    };

    const incoming: MatricBlock[] = [];
    const newRestTimeVisible: Record<string, boolean> = { ...restTimeVisible };

    const maxOrder = Math.max(
      ...((localSessionFormatBlocks || []) as any[]).map((b) => Number(b.order || 0)),
      -1
    );

    (localSessionFormatBlocks || []).forEach((blk: any) => {
      const blockType = blk.type || "MATRIX";
      const hasRestTime = blk.restTime && Number(blk.restTime) > 0;
      const isMaxOrder = Number(blk.order || 0) === maxOrder;

      if (blockType === "GROUP") {
        const bid = blk._id;
        const id = blk._id;
        const name = blk.name || `Group ${blk.order || 1}`;
        const workouts = Array.isArray(blk.workouts) ? blk.workouts : [];

        const processedWorkouts = workouts.map((w: any) => ({
          ...w,
          workPeriod: secToHHMMSS(Number(w?.workPeriod || 0)),
          restPeriod: secToHHMMSS(Number(w?.restPeriod || 0)),
          blockTiming: secToHHMMSS(Number(w?.blockTiming || 0)),
        }));

        if (hasRestTime && newRestTimeVisible[id] === undefined) {
          newRestTimeVisible[id] = true;
        }

        incoming.push({
          bid,
          id,
          name,
          type: "GROUP",
          fields: [],
          fieldValues: {},
          workouts: processedWorkouts,
          restTime: secToHHMMSS(Number(blk.restTime || 0)),
          isMaxOrder,
        });
      } else {
        const workouts = Array.isArray(blk.workouts) ? blk.workouts : [];
        workouts.forEach((w: any, idx: number) => {
          const bid = blk._id;
          const id = w?._id || `${blk._id}-${idx}`;
          const name = blk?.name || `Block ${blk.order || 1}-${idx + 1}`;
          const metrics = Array.isArray(w?.metrics) ? w.metrics : [];

          const fields: string[] = [];
          const fieldValues: Record<string, number> = {};

          metrics.forEach((m: any) => {
            let metricId = null;
            if (typeof m === "string") {
              metricId = m;
            } else {
              metricId =
                m?.metric ||
                m?._id ||
                m?.id ||
                m?.metric?._id ||
                m?.metric?.id ||
                null;
              if (metricId && m.value !== undefined && m.value !== null) {
                fieldValues[metricId] = Number(m.value);
              }
            }
            if (metricId) {
              fields.push(metricId);
            }
          });

          if (hasRestTime && newRestTimeVisible[id] === undefined) {
            newRestTimeVisible[id] = true;
          }

          incoming.push({
            bid,
            id,
            name,
            type: blockType,
            fields,
            fieldValues,
            workPeriod: secToHHMMSS(Number(w?.workPeriod || 0)),
            restPeriod: secToHHMMSS(Number(w?.restPeriod || 0)),
            blockTiming: secToHHMMSS(Number(w?.blockTiming || 0)),
            restTime: secToHHMMSS(Number(blk.restTime || 0)),
            isMaxOrder,
          });
        });
      }
    });

    setRestTimeVisible(newRestTimeVisible);

    setFormatBlocks((prev) => {
      const prevMap = new Map(prev.map((b) => [b.id, b]));
      return incoming.map((inc) => {
        const existing = prevMap.get(inc.id);
        // If existing exists, its restTime property might be empty if we haven't touched it,
        // but inc should have the correct one from API.
        // Actually, existing.restTime would be what's currently in state.
        return existing
          ? { ...inc, restTime: existing.restTime || inc.restTime }
          : inc;
      });
    });

    setIsDirty(false);
  }, [localSessionFormatBlocks, difficulty, setFormatBlocks, setIsDirty]);

  // Local modal helpers
  const toggleField = (metricId: string) => {
    setSelectedFields((prev) =>
      prev.includes(metricId)
        ? prev.filter((f) => f !== metricId)
        : [...prev, metricId],
    );
  };

  // NEW: Apply fields to GROUP workout
  const applyFieldsToGroupWorkout = () => {
    setIsDirty(true);
    if (
      !modalTargetBlockId ||
      !modalTargetWorkoutId ||
      selectedFields.length === 0
    ) {
      setShowFieldModal(false);
      setModalTargetBlockId(null);
      setModalTargetWorkoutId(null);
      return;
    }

    setFormatBlocks((prev) =>
      prev.map((block) => {
        if (block.id !== modalTargetBlockId || !block.workouts) return block;

        const updatedWorkouts = block.workouts.map((workout) => {
          const existingMetrics = workout.metrics || [];
          const remaining = Math.max(0, MAX_FIELDS - existingMetrics.length);
          if (remaining <= 0) return workout;

          const existingMetricIds = existingMetrics.map((m) => m.metric);
          const toAdd = selectedFields
            .filter((id) => !existingMetricIds.includes(id))
            .slice(0, remaining);

          if (toAdd.length === 0) return workout;

          const newMetrics = toAdd.map((metricId) => {
            const metricData = metricFieldOptions.find(
              (m) => m.id === metricId,
            );
            return {
              _id: `temp-${Date.now()}-${metricId}-${Math.random()
                .toString(36)
                .substr(2, 9)}`,
              name: metricData?.name || metricId,
              metric: metricId,
              value: 1,
              static: false,
            };
          });

          return {
            ...workout,
            metrics: [...existingMetrics, ...newMetrics],
          };
        });

        return {
          ...block,
          workouts: updatedWorkouts,
        };
      }),
    );

    setShowFieldModal(false);
    setModalTargetBlockId(null);
    setModalTargetWorkoutId(null);
    setSelectedFields([]);
  };

  const applyFields = () => {
    // If we're working with a group workout, use the group-specific logic
    if (modalTargetWorkoutId) {
      applyFieldsToGroupWorkout();
      return;
    }

    // Original logic for MATRIX/SINGLE blocks
    if (!modalTargetBlockId || selectedFields.length === 0) {
      setShowFieldModal(false);
      setModalTargetBlockId(null);
      return;
    }
    setIsDirty(true);
    setFormatBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== modalTargetBlockId) return b;
        const existing = b.fields || [];
        const remaining = Math.max(0, MAX_FIELDS - existing.length);
        if (remaining <= 0) return b;
        const toAdd = selectedFields
          .filter((id) => !existing.includes(id))
          .slice(0, remaining);
        if (toAdd.length === 0) return b;
        const nextFieldValues = { ...(b.fieldValues || {}) };
        toAdd.forEach((id) => {
          if (nextFieldValues[id] === undefined) nextFieldValues[id] = 1;
        });
        return {
          ...b,
          fields: [...existing, ...toAdd],
          fieldValues: nextFieldValues,
        };
      }),
    );
    setShowFieldModal(false);
    setModalTargetBlockId(null);
    setSelectedFields([]);
  };

  const removeFieldFromBlock = (blockId: string, metricId: string) => {
    setIsDirty(true);
    setFormatBlocks((prev) =>
      prev.map((b) =>
        b.id === blockId
          ? { ...b, fields: b.fields.filter((f) => f !== metricId) }
          : b,
      ),
    );
  };

  // NEW: Remove field from GROUP workout
  const removeFieldFromGroupWorkout = (
    blockId: string,
    workoutId: string,
    metricId: string,
  ) => {
    setIsDirty(true);
    setFormatBlocks((prev) =>
      prev.map((block) => {
        if (block.id !== blockId || !block.workouts) return block;

        return {
          ...block,
          workouts: block.workouts.map((workout) => {
            return {
              ...workout,
              metrics: workout.metrics.filter((m) => m.metric !== metricId),
            };
          }),
        };
      }),
    );
  };

  const effectiveDifficulty: "BEGINNER" | "ADVANCED" | "GENERAL" =
    (difficulty ?? "GENERAL") as any;

  const getMetricName = React.useCallback(
    (metricId: string) =>
      metricFieldOptions.find((o) => o.id === metricId)?.name || metricId,
    [metricFieldOptions],
  );

  const totalFormatTime = React.useMemo(() => {
    const timeToSec = (t: string = "00:00:00") => {
      const parts = t.split(":");
      const h = parseInt(parts[0] || "0", 10);
      const m = parseInt(parts[1] || "0", 10);
      const s = parseInt(parts[2] || "0", 10);
      return (
        (isNaN(h) ? 0 : h) * 3600 + (isNaN(m) ? 0 : m) * 60 + (isNaN(s) ? 0 : s)
      );
    };

    const totalSeconds = formatBlocks.reduce((acc, blk) => {
      let blockSec = 0;
      if (blk.type === "GROUP" && blk.workouts) {
        blockSec = blk.workouts.reduce((wAcc, w) => {
          return wAcc + timeToSec((w as any).blockTiming || "00:00:00");
        }, 0);
      } else {
        blockSec = timeToSec(blk.blockTiming || "00:00:00");
      }
      const restSec = timeToSec(blk.restTime || "00:00:00");
      return acc + blockSec + restSec;
    }, 0);

    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return [h, m, s].map((v) => String(v).padStart(2, "0")).join(":");
  }, [formatBlocks]);

  // Validation function: Check if difficulty needs to be selected
  const openAddExercise = () => {
    // Check if difficulty is null (subformat not selected when format has subformats)
    if (difficulty === null) {
      toast.error(
        "Please select a difficulty level (Beginner/Advanced) before adding exercises",
      );
      return;
    }
    setShowAddExerciseModal(true);
  };

  // Validation function for Create by Group
  const handleCreateByGroup = () => {
    // Check if difficulty is null (subformat not selected when format has subformats)
    if (difficulty === null) {
      toast.error(
        "Please select a difficulty level (Beginner/Advanced) before creating a group",
      );
      return;
    }
    setSelectedBlockId(null);
    setShowGroupModal(true);
  };

  const handleMetricChange = (
    blockId: string,
    oldMetricId: string,
    newMetricId: string,
  ) => {
    if (oldMetricId === newMetricId) return;

    setIsDirty(true);
    setFormatBlocks((prev) =>
      prev.map((block) => {
        if (block.id === blockId) {
          const updatedFields = block.fields.map((field) =>
            field === oldMetricId ? newMetricId : field,
          );
          const updatedFieldValues = { ...block.fieldValues };
          if (updatedFieldValues[oldMetricId] !== undefined) {
            updatedFieldValues[newMetricId] = updatedFieldValues[oldMetricId];
            delete updatedFieldValues[oldMetricId];
          }
          return {
            ...block,
            fields: updatedFields,
            fieldValues: updatedFieldValues,
          };
        }
        return block;
      }),
    );
  };

  // NEW: Handle metric change for group workouts
  const handleGroupWorkoutMetricTypeChange = (
    blockId: string,
    workoutId: string,
    oldMetricId: string,
    newMetricId: string,
  ) => {
    if (oldMetricId === newMetricId) return;

    setIsDirty(true);
    setFormatBlocks((prev) =>
      prev.map((block) => {
        if (block.id === blockId && block.workouts) {
          return {
            ...block,
            workouts: block.workouts.map((workout) => {
              return {
                ...workout,
                metrics: workout.metrics.map((metric) => {
                  if (metric.metric === oldMetricId) {
                    const newMetricData = metricFieldOptions.find(
                      (m) => m.id === newMetricId,
                    );
                    return {
                      ...metric,
                      metric: newMetricId,
                      name: newMetricData?.name || metric.name,
                    };
                  }
                  return metric;
                }),
              };
            }),
          };
        }
        return block;
      }),
    );
  };

  // Delete block modal state
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [blockToDelete, setBlockToDelete] = React.useState<{
    id: string; // workoutId (wrapper ID) or blockId
    name: string;
    groupId?: string; // New field to identify group deletion
    innerWorkoutId?: string; // The specific workout._id needed for the API
  } | null>(null);

  const [deleteBlock, { isLoading: isDeleting }] =
    useDeleteSessionMatriceBlockMutation();

  const [deleteGroupBlock, { isLoading: isDeletingGroup }] =
    useDeleteSessionMatriceGroupBlockMutation();

  const handleDeleteBlock = (blockId: string, blockName: string) => {
    setBlockToDelete({ id: blockId, name: blockName });
    setShowDeleteModal(true);
  };

  const confirmDeleteBlock = async () => {
    if (!blockToDelete) return;

    try {
      if (blockToDelete.groupId) {
        // Delete workout from group
        await deleteGroupBlock({
          groupId: blockToDelete.groupId,
          workoutId: blockToDelete.innerWorkoutId || blockToDelete.id, // Prefer inner ID if available
        }).unwrap();

        // Optimistic update for group workout
        setFormatBlocks((prev) =>
          prev.map((block) => {
            if (block.id === blockToDelete.groupId && block.workouts) {
              return {
                ...block,
                workouts: block.workouts.filter(
                  (w) => w._id !== blockToDelete.id,
                ),
              };
            }
            return block;
          }),
        );
      } else {
        // Existing block delete
        await deleteBlock({ blockId: blockToDelete.id }).unwrap();
        setFormatBlocks((prev) =>
          prev.filter((b) => b.id !== blockToDelete.id),
        );
      }

      setShowDeleteModal(false);
      setBlockToDelete(null);
    } catch (error) {
      console.error("Failed to delete block:", error);
      toast.error("Failed to delete block");
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setBlockToDelete(null);
  };

  // NEW: open delete modal for a single workout inside a group (passes workout id)
  const handleDeleteWorkoutInGroup = (
    groupId: string,
    workoutId: string, // wrapper ID for UI
    innerWorkoutId: string, // API ID
    workoutName?: string,
  ) => {
    setBlockToDelete({
      id: workoutId,
      name: workoutName || "Workout",
      groupId: groupId,
      innerWorkoutId: innerWorkoutId,
    });
    setShowDeleteModal(true);
  };

  // Render GROUP type block
  const renderGroupBlock = (blk: MatricBlock) => {
    const workouts = blk.workouts || [];

    return (
      <div key={blk.id} className="mb-6">
        <div
          className={`relative rounded-md border px-4 py-3 text-xs ${themeBox
            ? "bg-[#1a1a1a] border-[#2C2C2C]"
            : "bg-white border-gray-200"
            }`}
        >
          {/* Header */}
          <div
            className={`mb-3 pb-2 border-b flex items-center justify-between ${themeBox ? "border-[#2C2C2C]" : "border-gray-200"
              }`}
          >
            <h4
              className={`text-sm font-semibold ${themeBox ? "text-gray-200" : "text-gray-800"
                }`}
            >
              {blk.name}
            </h4>

            {showValidationErrors &&
              (blk.workouts || []).some(
                (w: any) => !w.metrics || w.metrics.length === 0,
              ) && (
                <div className="flex-1 px-4 flex justify-center">
                  <span className="text-red-500 font-medium text-[11px] animate-pulse">
                    No exercise values added. Please add at least one exercise field
                    to save or switch formats.
                  </span>
                </div>
              )}

            {showValidationErrors &&
              (blk.workouts || []).some(
                (w: any) => (w as any).workPeriod === "00:00:00" || (w as any).restPeriod === "00:00:00",
              ) && (
                <div className="flex-1 px-4 flex justify-center">
                  <span className="text-red-500 font-medium text-[11px] animate-pulse">
                    Please ensure all work and rest periods have valid time values.
                  </span>
                </div>
              )}

            {/* Right controls: Rest Time toggle + pill, Collapse/Expand */}
            <div className="flex items-center gap-2">
              {/* Add/Hide Rest Time button aligned right */}

              {/* Rest pill shown inline on right when enabled */}
              {restTimeVisible[blk.id] && (
                <div
                  className={`flex items-center gap-2 px-2 py-1 rounded border ${themeBox
                    ? "bg-[#181818] border-[#232323]"
                    : "bg-[#F9FFE8] border-[#C6D92C]"
                    }`}
                  style={{
                    borderRadius: "100px",
                    background: themeBox
                      ? "linear-gradient(92deg, rgba(31,31,31,0.95) 0%"
                      : "linear-gradient(92deg, rgba(237, 245, 192, 0.90) 46.67%, rgba(198,217,44,0.90) 116.75%)",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M20.5 12.5C20.5 17.1944 16.6944 21 12 21C7.30558 21 3.5 17.1944 3.5 12.5C3.5 7.80558 7.30558 4 12 4C16.6944 4 20.5 7.80558 20.5 12.5Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M5.88 18.7031L3.5 21.0031"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M18.1406 18.668L20.5006 20.998"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M5 3L2 6"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M22 6L19 3"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 8V12.5L14 14.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span
                    className={`text-xs font-medium ${themeBox ? "text-gray-200" : "text-gray-700"
                      }`}
                  >
                    Rest Time
                  </span>
                  {(() => {
                    const rest = blk.restTime || "00:00:00";
                    const [hh = "", mm = "", ss = ""] = rest.split(":");
                    return (
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={hh}
                          maxLength={2}
                          className={`w-6 text-center rounded border text-xs ${themeBox
                            ? "bg-[#232323] border-[#232323] text-gray-100"
                            : "bg-white border-gray-300 text-gray-700"
                            }`}
                          onChange={(e) => {
                            const val = e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 2);
                            const newRest = `${val}:${mm}:${ss}`;
                            handleRestTimeChange(blk.id, newRest);
                          }}
                          onBlur={() => handleRestTimeBlur(blk.id)}
                        />
                        <span className="text-gray-300">:</span>
                        <input
                          type="text"
                          value={mm}
                          maxLength={2}
                          className={`w-6 text-center rounded border text-xs ${themeBox
                            ? "bg-[#232323] border-[#232323] text-gray-100"
                            : "bg-white border-gray-300 text-gray-700"
                            }`}
                          onChange={(e) => {
                            const val = e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 2);
                            const newRest = `${hh}:${val}:${ss}`;
                            handleRestTimeChange(blk.id, newRest);
                          }}
                          onBlur={() => handleRestTimeBlur(blk.id)}
                        />
                        <span className="text-gray-300">:</span>
                        <input
                          type="text"
                          value={ss}
                          maxLength={2}
                          className={`w-6 text-center rounded border text-xs ${themeBox
                            ? "bg-[#232323] border-[#232323] text-gray-100"
                            : "bg-white border-gray-300 text-gray-700"
                            }`}
                          onChange={(e) => {
                            const val = e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 2);
                            const newRest = `${hh}:${mm}:${val}`;
                            handleRestTimeChange(blk.id, newRest);
                          }}
                          onBlur={() => handleRestTimeBlur(blk.id)}
                        />
                      </div>
                    );
                  })()}
                </div>
              )}

              <button
                type="button"
                onClick={() => toggleBlock(blk.id)}
                className="ml-1 p-1 rounded hover:bg-gray-200 dark:hover:bg-[#232323]"
                aria-label={expandedBlocks[blk.id] ? "Collapse" : "Expand"}
              >
                {expandedBlocks[blk.id] ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M6 15L12 9L18 15"
                      stroke={themeBox ? "#F3F3F6" : "#232323"}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M6 9L12 15L18 9"
                      stroke={themeBox ? "#F3F3F6" : "#232323"}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Group workouts */}
          {expandedBlocks[blk.id] && (
            <div className="space-y-4">
              {workouts.map((workout) => (
                <div
                  key={workout._id}
                  className={`pb-4 ${themeBox ? "border-[#2C2C2C] " : "border-gray-200"
                    }`}
                >
                  {/* Workout row */}
                  <div className="flex items-start gap-4">
                    {/* Left: workout name and metrics */}
                    <div className="flex-1">
                      <div
                        className={`text-xs mb-2 ${themeBox ? "text-gray-300" : "text-gray-700"
                          }`}
                      >
                        {workout.workout?.name || "--"}
                      </div>
                      <div className="flex flex-wrap gap-3">
                        {workout.metrics.map((metric) => (
                          <div
                            key={metric._id}
                            className="flex flex-col gap-1 w-[110px]"
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className={`flex-1 h-8 rounded border px-2 flex items-center ${themeBox
                                  ? "border-[#2C2C2C] bg-[#111]"
                                  : "border-gray-300 bg-white"
                                  }`}
                              >
                                <select
                                  value={metric.metric}
                                  onChange={(e) =>
                                    handleGroupWorkoutMetricTypeChange(
                                      blk.id,
                                      workout._id,
                                      metric.metric,
                                      e.target.value,
                                    )
                                  }
                                  className={`w-full bg-transparent outline-none text-[11px] ${themeBox ? "text-gray-200" : "text-gray-700"
                                    }`}
                                  style={{
                                    backgroundColor: themeBox
                                      ? "#000000"
                                      : "#ffffff",
                                    color: themeBox ? "#ffffff" : "#000000",
                                  }}
                                >
                                  <option
                                    value={metric.metric}
                                    style={{
                                      backgroundColor: themeBox
                                        ? "#000000"
                                        : "#ffffff",
                                      color: themeBox ? "#ffffff" : "#000000",
                                    }}
                                  >
                                    {metric.name}
                                  </option>
                                  {metricFieldOptions
                                    .filter(
                                      (opt) =>
                                        opt.id !== metric.metric &&
                                        !workout.metrics.some(
                                          (m) => m.metric === opt.id,
                                        ),
                                    )
                                    .map((opt) => (
                                      <option
                                        key={opt.id}
                                        value={opt.id}
                                        style={{
                                          backgroundColor: themeBox
                                            ? "#000000"
                                            : "#ffffff",
                                          color: themeBox
                                            ? "#ffffff"
                                            : "#000000",
                                        }}
                                      >
                                        {opt.name}
                                      </option>
                                    ))}
                                </select>
                              </div>
                              {/* REMOVE BUTTON for GROUP workout metric */}
                              <button
                                type="button"
                                onClick={() =>
                                  removeFieldFromGroupWorkout(
                                    blk.id,
                                    workout._id,
                                    metric.metric,
                                  )
                                }
                                className={`h-6 w-6 grid place-items-center rounded-sm border bg-[#5b2f2f] text-red-200 border-red-400 ${!themeBox
                                  ? "hover:bg-red-500 hover:text-white"
                                  : ""
                                  }`}
                                title="Remove field"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <path
                                    d="M12 21.5C16.4783 21.5 18.7175 21.5 20.1088 20.1088C21.5 18.7175 21.5 16.4783 21.5 12C21.5 7.52165 21.5 5.28248 20.1088 3.89124C18.7175 2.5 16.4783 2.5 12 2.5C7.52166 2.5 5.28249 2.5 3.89124 3.89124C2.5 5.28249 2.5 7.52166 2.5 12C2.5 16.4783 2.5 18.7175 3.89124 20.1088C5.28248 21.5 7.52165 21.5 12 21.5Z"
                                    stroke="#F3F3F6"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M15 9L9 14.9996M15 15L9 9.00039"
                                    stroke="#F3F3F6"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </button>
                            </div>
                            <div
                              className={`h-8 rounded border flex items-center justify-between px-2 ${themeBox
                                ? "border-[#2C2C2C] bg-[#0F0F0F]"
                                : "border-gray-300 bg-gray-100"
                                }`}
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  decrementGroupWorkoutMetric(
                                    blk.id,
                                    workout._id,
                                    metric.metric,
                                  )
                                }
                                className={`h-5 w-5 grid place-items-center rounded-full border text-[13px] hover:opacity-70 ${themeBox
                                  ? "border-gray-600 text-gray-300"
                                  : "border-gray-300 text-gray-600"
                                  }`}
                              >
                                −
                              </button>
                              <input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={String(metric.value)}
                                onChange={(e) =>
                                  handleGroupWorkoutMetricChange(
                                    blk.id,
                                    workout._id,
                                    metric.metric,
                                    e.target.value,
                                  )
                                }
                                className={`w-10 text-center text-[11px] bg-transparent outline-none ${themeBox ? "text-gray-200" : "text-gray-700"
                                  }`}
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  incrementGroupWorkoutMetric(
                                    blk.id,
                                    workout._id,
                                    metric.metric,
                                  )
                                }
                                className={`h-5 w-5 grid place-items-center rounded-full border text-[13px] hover:opacity-70 ${themeBox
                                  ? "border-gray-600 text-gray-300"
                                  : "border-gray-300 text-gray-600"
                                  }`}
                              >
                                +
                              </button>
                            </div>
                          </div>
                        ))}
                        {/* ADD FIELD BUTTON for GROUP workout */}
                        {workout.metrics.length < 5 && (
                          <button
                            type="button"
                            onClick={() => {
                              setModalTargetBlockId(blk.id);
                              setModalTargetWorkoutId(workout._id);
                              setSelectedFields([]);
                              setShowFieldModal(true);
                            }}
                            className={`mt-1 h-6 w-6 grid place-items-center rounded-sm border ${themeBox
                              ? "border-[#B6CB24] text-[#B6CB24] bg-[#343F1A] hover:opacity-90"
                              : "border-[#B6CB24] text-[#3A3A00] bg-[#EAF5C5] hover:bg-[#DDEEB0]"
                              }`}
                            title="Add fields"
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M12 5v14M5 12h14"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Middle: time inputs */}
                    <div className="flex flex-col">
                      <label
                        className={`text-[12px] mb-1 ${themeBox ? "text-gray-300" : "text-gray-600"
                          }`}
                      >
                        Work Period
                      </label>
                      <input
                        type="text"
                        value={(workout as any).workPeriod || "00:00:00"}
                        onChange={(e) =>
                          handleGroupWorkoutTimeChange(
                            blk.id,
                            workout._id,
                            "workPeriod",
                            e.target.value,
                          )
                        }
                        onBlur={() =>
                          handleGroupWorkoutTimeBlur(
                            blk.id,
                            workout._id,
                            "workPeriod",
                          )
                        }
                        placeholder="00:00:00"
                        maxLength={8}
                        className={`w-[120px] h-8 rounded border text-[11px] text-center outline-none ${timeErrors[`${blk.id}-${workout._id}-workPeriod`] ||
                          (showValidationErrors && (workout as any).workPeriod === "00:00:00")
                          ? "border-red-500"
                          : themeBox
                            ? "border-[#2C2C2C]"
                            : "border-gray-300"
                          } ${themeBox
                            ? "bg-[#0F0F0F] text-gray-200"
                            : "bg-white text-gray-700"
                          }`}
                      />
                      {timeErrors[`${blk.id}-${workout._id}-workPeriod`] && (
                        <span className="text-[10px] text-red-500 mt-1">
                          {timeErrors[`${blk.id}-${workout._id}-workPeriod`]}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <label
                        className={`text-[12px] mb-1 ${themeBox ? "text-gray-300" : "text-gray-600"
                          }`}
                      >
                        Rest Period
                      </label>
                      <input
                        type="text"
                        value={(workout as any).restPeriod || "00:00:00"}
                        onChange={(e) =>
                          handleGroupWorkoutTimeChange(
                            blk.id,
                            workout._id,
                            "restPeriod",
                            e.target.value,
                          )
                        }
                        onBlur={() =>
                          handleGroupWorkoutTimeBlur(
                            blk.id,
                            workout._id,
                            "restPeriod",
                          )
                        }
                        placeholder="00:00:00"
                        maxLength={8}
                        className={`w-[120px] h-8 rounded border text-[11px] text-center outline-none ${timeErrors[`${blk.id}-${workout._id}-restPeriod`] ||
                          (showValidationErrors && (workout as any).restPeriod === "00:00:00")
                          ? "border-red-500"
                          : themeBox
                            ? "border-[#2C2C2C]"
                            : "border-gray-300"
                          } ${themeBox
                            ? "bg-[#0F0F0F] text-gray-200"
                            : "bg-white text-gray-700"
                          }`}
                      />
                      {timeErrors[`${blk.id}-${workout._id}-restPeriod`] && (
                        <span className="text-[10px] text-red-500 mt-1">
                          {timeErrors[`${blk.id}-${workout._id}-restPeriod`]}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <label
                        className={`text-[12px] mb-1 ${themeBox ? "text-gray-300" : "text-gray-600"
                          }`}
                      >
                        Block Timing
                      </label>
                      <input
                        type="text"
                        readOnly
                        value={(workout as any).blockTiming || "00:00:00"}
                        // No onChange needed for read-only, but kept for safety/fallback if logic changes
                        placeholder="00:00:00"
                        className={`w-[120px] h-8 rounded border text-[11px] text-center outline-none opacity-60 cursor-not-allowed ${timeErrors[`${blk.id}-${workout._id}-blockTiming`]
                          ? "border-red-500"
                          : themeBox
                            ? "border-[#2C2C2C]"
                            : "border-gray-300"
                          } ${themeBox
                            ? "bg-[#0F0F0F] text-gray-200"
                            : "bg-white text-gray-700"
                          }`}
                      />
                      {timeErrors[`${blk.id}-${workout._id}-blockTiming`] && (
                        <span className="text-[10px] text-red-500 mt-1">
                          {timeErrors[`${blk.id}-${workout._id}-blockTiming`]}
                        </span>
                      )}
                    </div>

                    {/* Right: delete this workout (trash icon) */}
                    <button
                      type="button"
                      onClick={() =>
                        handleDeleteWorkoutInGroup(
                          blk.id,
                          workout._id,
                          workout.workout?._id, // Pass inner workout ID
                          workout.workout?.name,
                        )
                      }
                      className={`mt-5 h-6 w-6 grid place-items-center rounded ${themeBox
                        ? "text-red-400 hover:text-white hover:bg-red-600/20"
                        : "text-red-500 hover:bg-red-100"
                        }`}
                      title="Delete workout"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M12.0342 1.25C12.5408 1.25 12.9701 1.24949 13.3252 1.28125C13.694 1.31425 14.038 1.38536 14.3721 1.55859C14.5041 1.62709 14.6306 1.7068 14.75 1.7959C15.0518 2.02101 15.2663 2.29951 15.457 2.61719C15.6405 2.92284 15.8269 3.3089 16.0479 3.76465L16.5254 4.75H21C21.4142 4.75 21.75 5.08579 21.75 5.5C21.75 5.91421 21.4142 6.25 21 6.25H20.2051L19.6289 15.5713C19.5506 16.8374 19.4891 17.8405 19.3623 18.6396C19.2336 19.4506 19.0281 20.126 18.6162 20.7168C18.2393 21.2572 17.7534 21.7138 17.1904 22.0566C16.5754 22.4312 15.8887 22.5955 15.0713 22.6738C14.2659 22.751 13.2615 22.75 11.9932 22.75C10.723 22.75 9.71658 22.7512 8.91016 22.6738C8.09168 22.5953 7.40366 22.4311 6.78809 22.0557C6.22491 21.7122 5.74005 21.2543 5.36328 20.7129C4.9515 20.1211 4.74587 19.4449 4.61816 18.6328C4.49235 17.8326 4.43298 16.8283 4.35645 15.5605L3.79395 6.25H3C2.58579 6.25 2.25 5.91421 2.25 5.5C2.25 5.08579 2.58579 4.75 3 4.75H7.57031L7.97656 3.86035C8.19193 3.38787 8.37299 2.98768 8.55371 2.6709C8.7415 2.34175 8.95531 2.05271 9.26074 1.81836C9.38127 1.7259 9.50945 1.64359 9.64355 1.57227L9.77148 1.50879C10.0717 1.37296 10.3826 1.3123 10.7129 1.28223C11.076 1.24919 11.5151 1.25 12.0342 1.25ZM5.29688 6.25L5.85352 15.4697C5.93181 16.7666 5.98757 17.6867 6.09961 18.3994C6.20977 19.1001 6.36482 19.5261 6.59473 19.8564C6.85249 20.2268 7.18424 20.5395 7.56934 20.7744C7.91297 20.984 8.34764 21.1129 9.05371 21.1807C9.77189 21.2496 10.6939 21.25 11.9932 21.25C13.2905 21.25 14.2106 21.2494 14.9277 21.1807C15.6328 21.1131 16.0668 20.9845 16.4102 20.7754C16.7469 20.5703 17.0435 20.3058 17.2852 19.9951L17.3857 19.8584C17.6156 19.5286 17.7699 19.1036 17.8809 18.4043C17.9937 17.6926 18.0517 16.7737 18.1318 15.4785L18.7021 6.25H5.29688ZM12.0342 2.75C11.4881 2.75 11.1282 2.75092 10.8486 2.77637C10.5832 2.80053 10.448 2.84313 10.3477 2.89648C10.2868 2.92885 10.2285 2.96588 10.1738 3.00781C10.0837 3.07697 9.98855 3.18253 9.85645 3.41406C9.71731 3.65796 9.56738 3.9854 9.34082 4.48242L9.21875 4.75H14.8584L14.6982 4.41895C14.4657 3.93934 14.312 3.62365 14.1709 3.38867C14.0372 3.16597 13.9427 3.06462 13.8535 2.99805C13.8264 2.97784 13.7982 2.95839 13.7695 2.94043L13.6816 2.89062C13.5828 2.83935 13.4504 2.79856 13.1914 2.77539C12.9184 2.75098 12.5672 2.75 12.0342 2.75Z"
                          fill="url(#paint0_linear_1768_8044)"
                        />
                        <path
                          d="M9.5 16.5V10.5"
                          stroke="url(#paint1_linear_1768_8044)"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <path
                          d="M14.5 16.5V10.5"
                          stroke="url(#paint2_linear_1768_8044)"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                        <defs>
                          <linearGradient
                            id="paint0_linear_1768_8044"
                            x1="12"
                            y1="1.25"
                            x2="12"
                            y2="22.75"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stopColor="#D92626" />
                            <stop offset="1" stopColor="#AD1F1F" />
                          </linearGradient>
                          <linearGradient
                            id="paint1_linear_1768_8044"
                            x1="10"
                            y1="10.5"
                            x2="10"
                            y2="16.5"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stopColor="#D92626" />
                            <stop offset="1" stopColor="#AD1F1F" />
                          </linearGradient>
                          <linearGradient
                            id="paint2_linear_1768_8044"
                            x1="15"
                            y1="10.5"
                            x2="15"
                            y2="16.5"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stopColor="#D92626" />
                            <stop offset="1" stopColor="#AD1F1F" />
                          </linearGradient>
                        </defs>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="mt-1 flex justify-end">
          {(!blk.isMaxOrder || formatBlocks.length <= 1) && (
            <button
              type="button"
              onClick={() =>
                setRestTimeVisible((prev) => ({
                  ...prev,
                  [blk.id]: !prev[blk.id],
                }))
              }
              className={`flex items-center gap-2 px-3 py-1.5 text-[12px] font-medium border transition-colors backdrop-blur-sm ${themeBox ? "text-[#000000]" : "text-black"
                }`}
              style={{
                borderRadius: "100px",
                background: themeBox
                  ? " linear-gradient(92deg, rgba(237, 245, 192, 0.90) 46.67%, rgba(198, 217, 44, 0.90) 116.75%)"
                  : "linear-gradient(92deg, rgba(237, 245, 192, 0.90) 46.67%, rgba(198,217,44,0.90) 116.75%)",
              }}
              title="Toggle Rest Time"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M20.5 12.5C20.5 17.1944 16.6944 21 12 21C7.30558 21 3.5 17.1944 3.5 12.5C3.5 7.80558 7.30558 4 12 4C16.6944 4 20.5 7.80558 20.5 12.5Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M5.88 18.7031L3.5 21.0031"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M18.1406 18.668L20.5006 20.998"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M5 3L2 6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M22 6L19 3"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 8V12.5L14 14.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {restTimeVisible[blk.id] ? "Hide Rest Time" : "Add Rest Time"}
            </button>
          )}
        </div>
      </div>
    );
  };

  // Render MATRIX/SINGLE type block (existing design)
  const renderMatrixBlock = (blk: MatricBlock) => {
    return (
      <div key={blk.id} className="mb-6">
        <div
          className={`relative rounded-md border px-4 py-3 text-xs ${themeBox
            ? "bg-[#1a1a1a] border-[#2C2C2C]"
            : "bg-white border-gray-200"
            } overflow-visible pb-3`}
        >
          {/* Header */}
          <div
            className={`mb-3 pb-2 border-b flex items-center justify-between ${themeBox ? "border-[#2C2C2C]" : "border-gray-200"
              }`}
          >
            <h4
              className={`text-sm font-semibold ${themeBox ? "text-gray-200" : "text-gray-800"
                }`}
            >
              {blk.name}
            </h4>

            {showValidationErrors &&
              (!blk.fields || blk.fields.length === 0) && (
                <div className="flex-1 px-4 flex justify-center">
                  <span className="text-red-500 font-medium text-[11px] animate-pulse">
                    No exercise values added. Please add at least one exercise field
                    to save or switch formats
                  </span>
                </div>
              )}

            {showValidationErrors &&
              (blk.workPeriod === "00:00:00" || blk.restPeriod === "00:00:00") && (
                <div className="flex-1 px-4 flex justify-center">
                  <span className="text-red-500 font-medium text-[11px] animate-pulse">
                    Please ensure valid Work and Rest periods are set.
                  </span>
                </div>
              )}

            {restTimeVisible[blk.id] && (
              <div
                className={`flex items-center gap-2 ml-auto mr-2 px-2 py-1 rounded border ${themeBox
                  ? "bg-[#181818] border-[#232323]"
                  : "bg-[#F9FFE8] border-[#C6D92C]"
                  }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M20.5 12.5C20.5 17.1944 16.6944 21 12 21C7.30558 21 3.5 17.1944 3.5 12.5C3.5 7.80558 7.30558 4 12 4C16.6944 4 20.5 7.80558 20.5 12.5Z"
                    stroke="#F3F3F6"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M5.88 18.7031L3.5 21.0031"
                    stroke="#F3F3F6"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M18.1406 18.668L20.5006 20.998"
                    stroke="#F3F3F6"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M5 3L2 6"
                    stroke="#F3F3F6"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M22 6L19 3"
                    stroke="#F3F3F6"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 8V12.5L14 14.5"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span
                  className={`text-xs font-medium ${themeBox ? "text-gray-200" : "text-gray-700"
                    }`}
                >
                  Rest Time
                </span>
                {(() => {
                  const rest = blk.restTime || "00:00:00";
                  const [hh = "", mm = "", ss = ""] = rest.split(":");
                  return (
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        value={hh}
                        maxLength={2}
                        className={`w-6 text-center rounded border text-xs ${themeBox
                          ? "bg-[#232323] border-[#232323] text-gray-100"
                          : "bg-white border-gray-300 text-gray-700"
                          }`}
                        onChange={(e) => {
                          const val = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 2);
                          const newRest = `${val}:${mm}:${ss}`;
                          handleRestTimeChange(blk.id, newRest);
                        }}
                        onBlur={() => handleRestTimeBlur(blk.id)}
                      />
                      <span className="text-gray-300">:</span>
                      <input
                        type="text"
                        value={mm}
                        maxLength={2}
                        className={`w-6 text-center rounded border text-xs ${themeBox
                          ? "bg-[#232323] border-[#232323] text-gray-100"
                          : "bg-white border-gray-300 text-gray-700"
                          }`}
                        onChange={(e) => {
                          const val = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 2);
                          const newRest = `${hh}:${val}:${ss}`;
                          handleRestTimeChange(blk.id, newRest);
                        }}
                        onBlur={() => handleRestTimeBlur(blk.id)}
                      />
                      <span className="text-gray-300">:</span>
                      <input
                        type="text"
                        value={ss}
                        maxLength={2}
                        className={`w-6 text-center rounded border text-xs ${themeBox
                          ? "bg-[#232323] border-[#232323] text-gray-100"
                          : "bg-white border-gray-300 text-gray-700"
                          }`}
                        onChange={(e) => {
                          const val = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 2);
                          const newRest = `${hh}:${mm}:${val}`;
                          handleRestTimeChange(blk.id, newRest);
                        }}
                        onBlur={() => handleRestTimeBlur(blk.id)}
                      />
                    </div>
                  );
                })()}
              </div>
            )}

            <button
              type="button"
              onClick={() => toggleBlock(blk.id)}
              className="ml-2 p-1 rounded hover:bg-gray-200 dark:hover:bg-[#232323]"
              aria-label={expandedBlocks[blk.id] ? "Collapse" : "Expand"}
            >
              {expandedBlocks[blk.id] ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M6 15L12 9L18 15"
                    stroke={themeBox ? "#F3F3F6" : "#232323"}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M6 9L12 15L18 9"
                    stroke={themeBox ? "#F3F3F6" : "#232323"}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          </div>

          {/* Block Content */}
          {expandedBlocks[blk.id] && (
            <div className="flex items-start gap-4">
              <div className="flex flex-wrap gap-3 flex-1">
                {blk.fields.map((metricId) => {
                  const fieldValue = blk.fieldValues?.[metricId] ?? 1;
                  const metricName = getMetricName(metricId);
                  return (
                    <div
                      key={metricId}
                      className="flex flex-col gap-1 w-[110px]"
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={`flex-1 h-8 rounded border px-2 flex items-center ${themeBox
                            ? "border-[#2C2C2C] bg-[#111]"
                            : "border-gray-300 bg-white"
                            }`}
                        >
                          <select
                            value={metricId}
                            onChange={(e) =>
                              handleMetricChange(
                                blk.id,
                                metricId,
                                e.target.value,
                              )
                            }
                            className={`w-full bg-transparent outline-none text-[11px] ${themeBox ? "text-gray-200" : "text-gray-700"
                              }`}
                          >
                            <option
                              value={metricId}
                              style={{
                                backgroundColor: themeBox
                                  ? "#000000"
                                  : "#ffffff",
                                color: themeBox ? "#ffffff" : "#000000",
                              }}
                            >
                              {metricName}
                            </option>
                            {metricFieldOptions
                              .filter(
                                (opt) =>
                                  opt.id !== metricId &&
                                  !blk.fields.includes(opt.id),
                              )
                              .map((opt) => (
                                <option
                                  key={opt.id}
                                  value={opt.id}
                                  style={{
                                    backgroundColor: themeBox
                                      ? "#000000"
                                      : "#ffffff",
                                    color: themeBox ? "#ffffff" : "#000000",
                                  }}
                                >
                                  {opt.name}
                                </option>
                              ))}
                          </select>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFieldFromBlock(blk.id, metricId)}
                          className={`h-6 w-6 grid place-items-center rounded-sm border bg-[#5b2f2f] text-red-200 border-red-400 ${!themeBox ? "hover:bg-red-500 hover:text-white" : ""
                            }`}
                          title="Remove field"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M12 21.5C16.4783 21.5 18.7175 21.5 20.1088 20.1088C21.5 18.7175 21.5 16.4783 21.5 12C21.5 7.52165 21.5 5.28248 20.1088 3.89124C18.7175 2.5 16.4783 2.5 12 2.5C7.52166 2.5 5.28249 2.5 3.89124 3.89124C2.5 5.28249 2.5 7.52166 2.5 12C2.5 16.4783 2.5 18.7175 3.89124 20.1088C5.28248 21.5 7.52165 21.5 12 21.5Z"
                              stroke="#F3F3F6"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M15 9L9 14.9996M15 15L9 9.00039"
                              stroke="#F3F3F6"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </div>
                      <div
                        className={`h-8 rounded border flex items-center justify-between px-2 ${themeBox
                          ? "border-[#2C2C2C] bg-[#0F0F0F]"
                          : "border-gray-300 bg-gray-100"
                          }`}
                      >
                        <button
                          type="button"
                          onClick={() => decrementField(blk.id, metricId)}
                          className={`h-5 w-5 inline-flex items-center justify-center rounded-full border text-[13px] leading-none hover:opacity-70 ${themeBox
                            ? "border-gray-600 text-gray-300"
                            : "border-gray-300 text-gray-600"
                            }`}
                        >
                          −
                        </button>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={String(fieldValue)}
                          onChange={(e) =>
                            handleFieldValueChange(
                              blk.id,
                              metricId,
                              e.target.value,
                            )
                          }
                          className={`w-10 text-center text-[11px] bg-transparent outline-none ${themeBox ? "text-gray-200" : "text-gray-700"
                            }`}
                        />
                        <button
                          type="button"
                          onClick={() => incrementField(blk.id, metricId)}
                          className={`h-5 w-5 inline-flex items-center justify-center rounded-full border text-[13px] leading-none hover:opacity-70 ${themeBox
                            ? "border-gray-600 text-gray-300"
                            : "border-gray-300 text-gray-600"
                            }`}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  );
                })}
                {blk.fields.length < 5 && (
                  <button
                    type="button"
                    onClick={() => {
                      setModalTargetBlockId(blk.id);
                      setModalTargetWorkoutId(null);
                      setSelectedFields([]);
                      setShowFieldModal(true);
                    }}
                    className={`mt-1 h-6 w-6 grid place-items-center rounded-sm border ${themeBox
                      ? "border-[#B6CB24] text-[#B6CB24] bg-[#343F1A] hover:opacity-90"
                      : "border-[#B6CB24] text-[#3A3A00] bg-[#EAF5C5] hover:bg-[#DDEEB0]"
                      }`}
                    title="Add fields"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 5v14M5 12h14"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                )}
              </div>

              <div className="flex flex-col">
                <label
                  className={`text-[10px] mb-1 ${themeBox ? "text-gray-300" : "text-gray-600"
                    }`}
                >
                  Work Period
                </label>
                <input
                  type="text"
                  value={blk.workPeriod || "00:00:00"}
                  onChange={(e) =>
                    handleTimeChange(blk.id, "workPeriod", e.target.value)
                  }
                  onBlur={() => handleTimeBlur(blk.id, "workPeriod")}
                  placeholder="00:00:00"
                  maxLength={8}
                  className={`w-[120px] h-8 rounded border text-[11px] text-center outline-none ${timeErrors[`${blk.id}-workPeriod`] ||
                    (showValidationErrors && blk.workPeriod === "00:00:00")
                    ? "border-red-500"
                    : themeBox
                      ? "border-[#2C2C2C]"
                      : "border-gray-300"
                    } ${themeBox
                      ? "bg-[#0F0F0F] text-gray-200"
                      : "bg-white text-gray-700"
                    }`}
                />
                {timeErrors[`${blk.id}-workPeriod`] && (
                  <span className="text-[10px] text-red-500 mt-1">
                    {timeErrors[`${blk.id}-workPeriod`]}
                  </span>
                )}
              </div>
              <div className="flex flex-col">
                <label
                  className={`text-[10px] mb-1 ${themeBox ? "text-gray-300" : "text-gray-600"
                    }`}
                >
                  Rest Period
                </label>
                <input
                  type="text"
                  value={blk.restPeriod || "00:00:00"}
                  onChange={(e) =>
                    handleTimeChange(blk.id, "restPeriod", e.target.value)
                  }
                  onBlur={() => handleTimeBlur(blk.id, "restPeriod")}
                  placeholder="00:00:00"
                  maxLength={8}
                  className={`w-[120px] h-8 rounded border text-[11px] text-center outline-none ${timeErrors[`${blk.id}-restPeriod`] ||
                    (showValidationErrors && blk.restPeriod === "00:00:00")
                    ? "border-red-500"
                    : themeBox
                      ? "border-[#2C2C2C]"
                      : "border-gray-300"
                    } ${themeBox
                      ? "bg-[#0F0F0F] text-gray-200"
                      : "bg-white text-gray-700"
                    }`}
                />
                {timeErrors[`${blk.id}-restPeriod`] && (
                  <span className="text-[10px] text-red-500 mt-1">
                    {timeErrors[`${blk.id}-restPeriod`]}
                  </span>
                )}
              </div>
              <div className="flex flex-col">
                <label
                  className={`text-[10px] mb-1 ${themeBox ? "text-gray-300" : "text-gray-600"
                    }`}
                >
                  Block Timing
                </label>
                <input
                  type="text"
                  readOnly
                  value={blk.blockTiming || "00:00:00"}
                  // No onChange needed for read-only
                  placeholder="00:00:00"
                  className={`w-[120px] h-8 rounded border text-[11px] text-center outline-none opacity-60 cursor-not-allowed ${timeErrors[`${blk.id}-blockTiming`]
                    ? "border-red-500"
                    : themeBox
                      ? "border-[#2C2C2C]"
                      : "border-gray-300"
                    } ${themeBox
                      ? "bg-[#0F0F0F] text-gray-200"
                      : "bg-white text-gray-700"
                    }`}
                />
                {timeErrors[`${blk.id}-blockTiming`] && (
                  <span className="text-[10px] text-red-500 mt-1">
                    {timeErrors[`${blk.id}-blockTiming`]}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => handleDeleteBlock(blk.bid || blk.id, blk.name)}
                className={`mt-5 h-6 w-6 grid place-items-center rounded ${themeBox
                  ? "text-red-400 hover:text-white hover:bg-red-600/20"
                  : "text-red-500 hover:bg-red-100"
                  }`}
                title="Delete block"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M12.0342 1.25C12.5408 1.25 12.9701 1.24949 13.3252 1.28125C13.694 1.31425 14.038 1.38536 14.3721 1.55859C14.5041 1.62709 14.6306 1.7068 14.75 1.7959C15.0518 2.02101 15.2663 2.29951 15.457 2.61719C15.6405 2.92284 15.8269 3.3089 16.0479 3.76465L16.5254 4.75H21C21.4142 4.75 21.75 5.08579 21.75 5.5C21.75 5.91421 21.4142 6.25 21 6.25H20.2051L19.6289 15.5713C19.5506 16.8374 19.4891 17.8405 19.3623 18.6396C19.2336 19.4506 19.0281 20.126 18.6162 20.7168C18.2393 21.2572 17.7534 21.7138 17.1904 22.0566C16.5754 22.4312 15.8887 22.5955 15.0713 22.6738C14.2659 22.751 13.2615 22.75 11.9932 22.75C10.723 22.75 9.71658 22.7512 8.91016 22.6738C8.09168 22.5953 7.40366 22.4311 6.78809 22.0557C6.22491 21.7122 5.74005 21.2543 5.36328 20.7129C4.9515 20.1211 4.74587 19.4449 4.61816 18.6328C4.49235 17.8326 4.43298 16.8283 4.35645 15.5605L3.79395 6.25H3C2.58579 6.25 2.25 5.91421 2.25 5.5C2.25 5.08579 2.58579 4.75 3 4.75H7.57031L7.97656 3.86035C8.19193 3.38787 8.37299 2.98768 8.55371 2.6709C8.7415 2.34175 8.95531 2.05271 9.26074 1.81836C9.38127 1.7259 9.50945 1.64359 9.64355 1.57227L9.77148 1.50879C10.0717 1.37296 10.3826 1.3123 10.7129 1.28223C11.076 1.24919 11.5151 1.25 12.0342 1.25ZM5.29688 6.25L5.85352 15.4697C5.93181 16.7666 5.98757 17.6867 6.09961 18.3994C6.20977 19.1001 6.36482 19.5261 6.59473 19.8564C6.85249 20.2268 7.18424 20.5395 7.56934 20.7744C7.91297 20.984 8.34764 21.1129 9.05371 21.1807C9.77189 21.2496 10.6939 21.25 11.9932 21.25C13.2905 21.25 14.2106 21.2494 14.9277 21.1807C15.6328 21.1131 16.0668 20.9845 16.4102 20.7754C16.7469 20.5703 17.0435 20.3058 17.2852 19.9951L17.3857 19.8584C17.6156 19.5286 17.7699 19.1036 17.8809 18.4043C17.9937 17.6926 18.0517 16.7737 18.1318 15.4785L18.7021 6.25H5.29688ZM12.0342 2.75C11.4881 2.75 11.1282 2.75092 10.8486 2.77637C10.5832 2.80053 10.448 2.84313 10.3477 2.89648C10.2868 2.92885 10.2285 2.96588 10.1738 3.00781C10.0837 3.07697 9.98855 3.18253 9.85645 3.41406C9.71731 3.65796 9.56738 3.9854 9.34082 4.48242L9.21875 4.75H14.8584L14.6982 4.41895C14.4657 3.93934 14.312 3.62365 14.1709 3.38867C14.0372 3.16597 13.9427 3.06462 13.8535 2.99805C13.8264 2.97784 13.7982 2.95839 13.7695 2.94043L13.6816 2.89062C13.5828 2.83935 13.4504 2.79856 13.1914 2.77539C12.9184 2.75098 12.5672 2.75 12.0342 2.75Z"
                    fill="url(#paint0_linear_1768_8044)"
                  />
                  <path
                    d="M9.5 16.5V10.5"
                    stroke="url(#paint1_linear_1768_8044)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M14.5 16.5V10.5"
                    stroke="url(#paint2_linear_1768_8044)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <defs>
                    <linearGradient
                      id="paint0_linear_1768_8044"
                      x1="12"
                      y1="1.25"
                      x2="12"
                      y2="22.75"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#D92626" />
                      <stop offset="1" stopColor="#AD1F1F" />
                    </linearGradient>
                    <linearGradient
                      id="paint1_linear_1768_8044"
                      x1="10"
                      y1="10.5"
                      x2="10"
                      y2="16.5"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#D92626" />
                      <stop offset="1" stopColor="#AD1F1F" />
                    </linearGradient>
                    <linearGradient
                      id="paint2_linear_1768_8044"
                      x1="15"
                      y1="10.5"
                      x2="15"
                      y2="16.5"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#D92626" />
                      <stop offset="1" stopColor="#AD1F1F" />
                    </linearGradient>
                  </defs>
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* <div className="mt-1 flex justify-end">
          {(!blk.isMaxOrder || formatBlocks.length <= 1) && (
            <button
              type="button"
              onClick={() =>
                setRestTimeVisible((prev) => ({
                  ...prev,
                  [blk.id]: !prev[blk.id],
                }))
              }
              className={`flex items-center gap-2 px-3 py-1.5 text-[12px] font-medium border transition-colors backdrop-blur-sm ${themeBox ? "text-[#000000]" : "text-black"
                }`}
              style={{
                borderRadius: "100px",
                background: themeBox
                  ? " linear-gradient(92deg, rgba(237, 245, 192, 0.90) 46.67%, rgba(198, 217, 44, 0.90) 116.75%)"
                  : "linear-gradient(92deg, rgba(237, 245, 192, 0.90) 46.67%, rgba(198,217,44,0.90) 116.75%)",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M20.5 12.5C20.5 17.1944 16.6944 21 12 21C7.30558 21 3.5 17.1944 3.5 12.5C3.5 7.80558 7.30558 4 12 4C16.6944 4 20.5 7.80558 20.5 12.5Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M5.88 18.7031L3.5 21.0031"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M18.1406 18.668L20.5006 20.998"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M5 3L2 6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M22 6L19 3"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 8V12.5L14 14.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {restTimeVisible[blk.id] ? "Hide Rest Time" : "Add Rest Time"}
            </button>
          )}
        </div> */}
      </div>
    );
  };

  return (
    <div className="flex flex-col p-4">
      {/* Show message when difficulty is required but not selected */}
      {difficulty === null && (
        <div
          className={`mb-4 p-3 rounded-md text-sm border ${themeBox
            ? "bg-[#2B2B2B] border-[#3A3A3A] text-[#FFD23F]"
            : "bg-yellow-50 border-yellow-200 text-yellow-800"
            }`}
          role="status"
          aria-live="polite"
        >
          Please select a difficulty level (Beginner / Advanced) from the format
          sidebar before editing or adding exercises.
        </div>
      )}

      <div className="space-y-4 max-h-[480px] overflow-y-auto pr-2">
        {formatBlocks.map((blk) =>
          blk.type === "GROUP" ? renderGroupBlock(blk) : renderMatrixBlock(blk),
        )}
      </div>

      <div
        className={`mt-4 flex items-center justify-between rounded-md px-4 py-3 ${themeBox
          ? "bg-[#141414] border border-[#232323]"
          : "bg-gray-100 border border-gray-300"
          }`}
      >
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={openAddExercise}
            disabled={isCreatingExercise || difficulty === null}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium ${difficulty === null ? "opacity-60 cursor-not-allowed" : ""
              }`}
            style={{
              background:
                "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198,217,44,0.80) 0%, rgba(83,91,16,0.80) 100%)",
              color: "#000",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M7.75 11.5H16.25C17.6429 11.5 18 11.9094 18 13.25C18 14.6429 17.5906 15 16.25 15H7.75C6.3571 15 6 14.5906 6 13.25C6 11.8571 6.40936 11.5 7.75 11.5Z"
                stroke="#F3F3F6"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7.75 15H16.25C17.6429 15 18 15.4094 18 16.75C18 18.1429 17.5906 18.5 16.25 18.5H7.75C6.3571 18.5 6 18.0906 6 16.75C6 15.3571 6.40936 15 7.75 15Z"
                stroke="#C6D92C"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7.75 18.5H16.25C17.6429 18.5 18 18.9094 18 20.25C18 21.6429 17.5906 22 16.25 22H7.75C6.3571 22 6 21.5906 6 20.25C6 18.8571 6.40936 18.5 7.75 18.5Z"
                stroke="#F3F3F6"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M22 7.49917L21.201 6.50039C20.6109 5.76277 20.3158 5.39396 19.9051 5.19656C19.5608 5.03105 19.1731 5.00432 18.5 5M2 7.49917L2.79902 6.50039C3.38912 5.76277 3.68417 5.39396 4.09487 5.19656C4.43923 5.03105 4.82691 5.00432 5.5 5M16 5H8"
                stroke="#C6D92C"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M16 2V11.5M8 2V11.5"
                stroke="#F3F3F6"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {isCreatingExercise ? "Adding..." : "Add Exercises"}
          </button>
          <button
            type="button"
            onClick={handleCreateByGroup}
            disabled={difficulty === null}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium ${difficulty === null ? "opacity-60 cursor-not-allowed" : ""
              }`}
            style={{
              background:
                "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198,217,44,0.80) 0%, rgba(83,91,16,0.80) 100%)",
              color: "#000",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="20"
              viewBox="0 0 22 20"
              fill="none"
            >
              <path
                d="M6.75 4.75H15.5C17.6067 4.75 18.66 4.75 19.4167 5.25559C19.7443 5.47447 20.0255 5.75572 20.2444 6.08329C20.75 6.83996 20.75 7.8933 20.75 10C20.75 13.5112 20.75 15.2667 19.9073 16.5279C19.5426 17.0738 19.0738 17.5426 18.5279 17.9073C17.2667 18.75 15.5112 18.75 12 18.75H10.75C6.03595 18.75 3.67893 18.75 2.21447 17.2855C0.75 15.8211 0.75 13.464 0.75 8.75V5.69427C0.75 3.8778 0.75 2.96956 1.13032 2.28806C1.40142 1.80227 1.80227 1.40142 2.28806 1.13032C2.96956 0.75 3.8778 0.75 5.69427 0.75C6.85802 0.75 7.4399 0.75 7.94926 0.94101C9.1122 1.37712 9.5918 2.43358 10.1166 3.48313L10.75 4.75"
                stroke="#F3F3F6"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            Create by Group
          </button>
        </div>
        <div
          className={`flex items-center gap-2 px-4 py-2 rounded-md ${themeBox ? "bg-[#0A0A0A]" : "bg-white border border-gray-200"
            }`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M20.5 12.5C20.5 17.1944 16.6944 21 12 21C7.30558 21 3.5 17.1944 3.5 12.5C3.5 7.80558 7.30558 4 12 4C16.6944 4 20.5 7.80558 20.5 12.5Z"
              stroke={themeBox ? "#F3F3F6" : "#1F1F1F"}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M5.88 18.7031L3.5 21.0031"
              stroke={themeBox ? "#F3F3F6" : "#1F1F1F"}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M18.1406 18.668L20.5006 20.998"
              stroke={themeBox ? "#F3F3F6" : "#1F1F1F"}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M5 3L2 6"
              stroke={themeBox ? "#F3F3F6" : "#1F1F1F"}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M22 6L19 3"
              stroke={themeBox ? "#F3F3F6" : "#1F1F1F"}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 8V12.5L14 14.5"
              stroke="#C6D92C"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span
            className={`text-xs font-medium ${themeBox ? "text-gray-200" : "text-gray-700"
              }`}
          >
            Total Time
          </span>
          <span
            className={`text-sm font-bold ${themeBox ? "text-white" : "text-gray-900"
              }`}
          >
            {totalFormatTime}
          </span>
        </div>
      </div>

      <CreateGroupModal
        isOpen={showGroupModal}
        onClose={() => {
          setShowGroupModal(false);
          setSelectedBlockId(null);
        }}
        themeBox={themeBox}
        blockId={selectedBlockId || ""}
        sessionData={localSessionFormatBlocks}
        categoryId={categoryId || ""}
        sessionFormatId={sessionFormatId}
        difficulty={effectiveDifficulty}
      />

      {showFieldModal && (
        <FieldSelectionModal
          open={showFieldModal}
          onClose={() => {
            setShowFieldModal(false);
            setModalTargetBlockId(null);
            setModalTargetWorkoutId(null);
            setSelectedFields([]);
          }}
          themeBox={themeBox}
          fieldOptions={metricFieldOptions}
          selectedFields={selectedFields}
          toggleField={toggleField}
          maxFields={MAX_FIELDS}
          modalTargetBlockId={modalTargetBlockId}
          formatBlocks={formatBlocks}
          applyFields={applyFields}
          existingFieldIds={
            modalTargetWorkoutId && modalTargetBlockId
              ? (() => {
                const groupBlock = formatBlocks.find(
                  (b) => b.id === modalTargetBlockId && b.type === "GROUP",
                );
                if (!groupBlock?.workouts) return [];
                const workout = groupBlock.workouts.find(
                  (w) => w._id === modalTargetWorkoutId,
                );
                return (workout?.metrics || []).map(
                  (m: any) => m.metric || m._id,
                );
              })()
              : []
          }
        />
      )}

      {showAddExerciseModal && (
        <AddExerciseModal
          open={showAddExerciseModal}
          onClose={() => setShowAddExerciseModal(false)}
          themeBox={themeBox}
          addModalSelection={addModalSelection}
          setAddModalSelection={setAddModalSelection}
          confirmAddExercise={confirmAddExercise}
          difficulty={effectiveDifficulty || "GENERAL"}
          sessionFormatId={sessionFormatId}
          existingExerciseIds={existingExerciseIds}
        />
      )}

      {showDeleteModal && blockToDelete && (
        <DeleteBlockModal
          open={showDeleteModal}
          onClose={closeDeleteModal}
          themeBox={themeBox}
          blockId={blockToDelete.id}
          blockName={blockToDelete.name}
          onConfirmDelete={confirmDeleteBlock}
          isDeleting={isDeleting}
        />
      )}
    </div>
  );
};

export default MetricSessionFormat;
